import React, { useState } from 'react'
import { API_ENDPOINTS } from '../../../config/api'
import apiClient from '../../../config/api'
import Swal from 'sweetalert2'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

const LaybyPayment = ({ layby, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // Payment methods (same as Checkout.js)
  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'usd_cash', label: 'USD Cash' },
    { value: 'zig_cash', label: 'ZIG Cash' },
    { value: 'ecocash_usd', label: 'Ecocash USD' },
    { value: 'ecocash_zig', label: 'Ecocash ZIG' },
    { value: 'bank_transfer_zig', label: 'Bank Transfer ZIG' },
    { value: 'bank_transfer_usd', label: 'Bank Transfer USD' },
    { value: 'card', label: 'Card' },
  ]

  const balance = Number(layby.balance) || 0

  const validateForm = () => {
    const newErrors = {}

    if (!amount || Number(amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    } else if (Number(amount) > balance) {
      newErrors.amount = `Amount cannot exceed remaining balance of $${balance.toFixed(2)}`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please correct the errors in the form',
        confirmButtonColor: brandColors.primary,
      })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        amount: Number(parseFloat(amount).toFixed(2)),
        payment_method: paymentMethod,
        notes: notes.trim() || undefined,
      }

      await apiClient.post(API_ENDPOINTS.LAYBY_PAYMENT(layby.id), payload)

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Payment recorded successfully',
        confirmButtonColor: brandColors.primary,
      })

      // Reset form
      setAmount('')
      setPaymentMethod('cash')
      setNotes('')
      setErrors({})

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Error recording payment:', error)
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to record payment. Please try again.'
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: brandColors.primary,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (value) => {
    return `$${Number(value).toFixed(2)}`
  }

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cash-coin me-2" />
              Make Payment
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-info" role="alert">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <strong>Layby:</strong> {layby.layby_number}
                  </span>
                </div>
                <div className="mt-2">
                  <span>
                    <strong>Customer:</strong> {layby.customer_name}
                  </span>
                </div>
                <div className="mt-2">
                  <span>
                    <strong>Remaining Balance:</strong>{' '}
                    <span className="fw-bold text-primary">
                      {formatCurrency(balance)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Payment Amount <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    max={balance}
                    placeholder="Enter payment amount"
                    required
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">{errors.amount}</div>
                  )}
                </div>
                <small className="text-muted">
                  Maximum: {formatCurrency(balance)}
                </small>
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setAmount(balance.toFixed(2))}
                  >
                    Pay Full Balance
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Payment Method <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  required
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Notes (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this payment..."
                />
              </div>

              {amount && Number(amount) > 0 && (
                <div className="alert alert-light border">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Payment Amount:</span>
                    <span className="fw-bold">{formatCurrency(amount)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-0">
                    <span>New Balance:</span>
                    <span
                      className={`fw-bold ${
                        balance - Number(amount) > 0 ? 'text-warning' : 'text-success'
                      }`}
                    >
                      {formatCurrency(Math.max(0, balance - Number(amount)))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white"
                disabled={isSubmitting || !amount || Number(amount) <= 0}
                style={{
                  background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                  border: 'none',
                }}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2" />
                    Record Payment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LaybyPayment
